import { sweetAlert, AlertType, AlertTitle, baseAPIUrl, httpPostHandleResponse, bindAndInitSelect2, isNullOrEmptyOrZero, formatDate_MMMDDYYYY, getHtml_HandleBar } from "../utils/common";
import { Loader } from "../services/loaderService";
import http, { GetToken } from "../services/httpService";
import moment = require("moment");

export interface DocumentRequestsConfig {
    containerSelector: string;
    employeeId: any;
    clientId: any;
}

export class DocumentRequests {
    private config: DocumentRequestsConfig;
    private loader: Loader = new Loader();

    private readonly controller: string = baseAPIUrl() ? `${baseAPIUrl()}/EmployeeDocumentRequest` : '/EmployeeDocumentRequest';

    private $container: JQuery<HTMLElement>;
    private $ddlDocumentType: JQuery<HTMLElement>;
    private $ddlPriority: JQuery<HTMLElement>;
    private $txtRequiredDate: JQuery<HTMLElement>;
    private $txtRemarks: JQuery<HTMLElement>;
    private $btnSubmit: JQuery<HTMLElement>;
    private $tbody: JQuery<HTMLElement>;

    private documentTypes: any[] = [];

    private readonly priorities: any[] = [
        { Id: 3, Name: "High" },
        { Id: 2, Name: "Medium" },
        { Id: 1, Name: "Low" }
    ];

    constructor(config: DocumentRequestsConfig) {
        this.config = config;

        this.$container = $(config.containerSelector);
        this.$ddlDocumentType = this.$container.find('.ddl-request-doc-type');
        this.$ddlPriority = this.$container.find('.ddl-request-priority');
        this.$txtRequiredDate = this.$container.find('.txt-request-required-date');
        this.$txtRemarks = this.$container.find('.txt-request-remarks');
        this.$btnSubmit = this.$container.find('.btn-submit-doc-request');
        this.$tbody = this.$container.find('.tbody-doc-requests');

        this.bindEvents();
        this.init();
    }

    private bindEvents() {
        this.$btnSubmit.off('click').on('click', () => this.submit());
    }

    public async init() {
        if (!this.$container.length) {
            return;
        }

        bindAndInitSelect2(this.$ddlPriority, this.priorities);
        this.$ddlPriority.val(String(2)).trigger('change');

        (this.$txtRequiredDate as any).flatpickr({
            dateFormat: "M d, Y",
            minDate: "today",
            allowInput: true,
            clickOpens: true
        });

        await this.loadDocumentTypes();
        await this.loadHistory();
    }

    private async loadDocumentTypes() {
        try {
            if (isNullOrEmptyOrZero(this.config.clientId)) {
                this.documentTypes = [];
                bindAndInitSelect2(this.$ddlDocumentType, []);
                return;
            }

            GetToken();

            const policyResponse = await http.post(`${baseAPIUrl()}/DocumentType/GetDocumentPoliciesByClientId`, {
                ClientId: this.config.clientId
            });
            const policyData = httpPostHandleResponse(policyResponse) || [];

            const allTypesResponse = await http.post(`${baseAPIUrl()}/DocumentType/GetAllActiveForClient`, {});
            const allTypes = httpPostHandleResponse(allTypesResponse) || [];

            this.documentTypes = policyData
                .filter((p: any) => p.Active)
                .map((p: any) => {
                    const match = allTypes.find((t: any) => t.Id === p.DocumentTypeId);
                    return match ? { Id: p.DocumentTypeId, Name: match.Name } : null;
                })
                .filter((t: any) => t !== null);

            bindAndInitSelect2(this.$ddlDocumentType, this.documentTypes);
            this.$ddlDocumentType.prepend('<option value=""></option>');
            this.$ddlDocumentType.val('').trigger('change');
        } catch (error) {
            console.error("DocumentRequests: document type load failed", error);
            this.documentTypes = [];
        }
    }

    public async loadHistory() {
        if (isNullOrEmptyOrZero(this.config.employeeId)) {
            this.renderHistory([]);
            return;
        }

        try {
            GetToken();
            const response = await http.post(`${this.controller}/GetByEmployeeId/${this.config.employeeId}`);
            const data = httpPostHandleResponse(response) || [];
            this.renderHistory(data);
        } catch (error) {
            console.error("DocumentRequests: history load failed", error);
            this.renderHistory([]);
        }
    }

    private renderHistory(requests: any[]) {
        this.$tbody.empty();

        if (!requests || requests.length === 0) {
            this.$tbody.append('<tr><td colspan="9" class="text-center text-muted">No document requests yet.</td></tr>');
            return;
        }

        requests.forEach(r => this.$tbody.append(this.createRow(r)));
    }

    private createRow(request: any): string {
        const status = request.Status || '';
        const priority = Number(request.Priority) || 0;

        return getHtml_HandleBar('#tmpl_doc_request_row', {
            RequestId: request.Id,
            DocumentTypeName: request.DocumentTypeName,
            PriorityName: request.PriorityName,
            IsHigh: priority === 3,
            IsMedium: priority === 2,
            IsLow: priority === 1,
            RequiredDateStr: this.formatDate(request.RequiredDate),
            Remarks: request.Remarks || '-',
            IsPending: status === 'Pending',
            IsCompleted: status === 'Completed',
            IsRejected: status === 'Rejected',
            RejectionRemarks: request.RejectionRemarks || '',
            RequestedOnStr: this.formatDate(request.RequestedOn),
            ModifiedByName: request.ModifiedByName || '-',
            ModifiedOnStr: this.formatDate(request.ModifiedOn)
        });
    }

    private formatDate(value: any): string {
        if (!value) {
            return '-';
        }
        const formatted = formatDate_MMMDDYYYY(value);
        return (!formatted || formatted === "Jan 1, 2001" || formatted === "Jan 1, 0001") ? '-' : formatted;
    }

    private async submit() {
        const documentTypeId = this.$ddlDocumentType.val();
        const priority = this.$ddlPriority.val();
        const requiredDateValue = this.$txtRequiredDate.val() as string;

        if (isNullOrEmptyOrZero(this.config.employeeId)) {
            sweetAlert(AlertType.Warning, AlertTitle.Warning, "Employee details are not loaded yet. Please reload the page.");
            return;
        }

        if (!documentTypeId) {
            sweetAlert(AlertType.Warning, AlertTitle.Warning, "Please select a Document Type.");
            return;
        }

        if (!priority) {
            sweetAlert(AlertType.Warning, AlertTitle.Warning, "Please select a Priority.");
            return;
        }

        if (!requiredDateValue) {
            sweetAlert(AlertType.Warning, AlertTitle.Warning, "Please select a Required Date.");
            return;
        }

        const requiredDate = moment(requiredDateValue, ["MMM D, YYYY", "MMM DD, YYYY", "YYYY-MM-DD"], true);

        if (!requiredDate.isValid()) {
            sweetAlert(AlertType.Warning, AlertTitle.Warning, "Please select a valid Required Date.");
            return;
        }

        const dataToSend: any = {
            EmployeeId: this.config.employeeId,
            DocumentTypeId: documentTypeId,
            Priority: priority,
            RequiredDate: requiredDate.format("YYYY-MM-DD"),
            Remarks: this.$txtRemarks.val()
        };

        this.loader.showLoader();
        GetToken();

        try {
            const response = await http.post(`${this.controller}/Add`, dataToSend);

            const data = httpPostHandleResponse(response);

            if (data) {
                this.clearForm();
                await this.loadHistory();
                sweetAlert(AlertType.Success, AlertTitle.Success, "Your document request has been submitted.");
            }
        } finally {
            this.loader.hideLoader();
        }
    }

    private clearForm() {
        this.$ddlDocumentType.val('').trigger('change');
        this.$ddlPriority.val(String(2)).trigger('change');
        this.$txtRemarks.val('');
        const picker = (this.$txtRequiredDate[0] as any)?._flatpickr;
        if (picker) {
            picker.clear();
        } else {
            this.$txtRequiredDate.val('');
        }
    }
}
